import axios from 'axios';
import {
    GOALS,
    PROCESSES,
    EDGE_TYPE,
    FLOW_TYPE,
    GOAL_EMAIL_LINK_CLICKED,
    GOAL_EMAIL_CONFIRM,
    GOAL_TASK_COMPLETED,
    SEQUENCE_START,
    NO_VALID_INCOMING_FLOW_ERROR,
    VALID_INCOMING_FLOW,
    LEADSOURCE_TYPE,
    DECISION_TYPE,
    TIMERS,
} from '../editor.constants';
import { utils } from '../legacy-utils';
import { findIncomingCells, findLockedIncomingCells, findOutgoingCells } from './automation-edge-utils';

const BASE_FUNNEL_URL = '/app/funnel';
const NON_PROCESS_TYPES = [LEADSOURCE_TYPE, DECISION_TYPE, ...Object.values(GOALS)];
const TIMER_TYPES = [...Object.values(TIMERS)];

export const findChildren = (cellId, cells) => {
    if (!cells?.filter) return [];

    return cells.filter((cell) => cell.meta.parent === cellId);
};

export const findParent = (parentId, cells) => {
    if (!cells?.filter) return [];

    return cells.filter((cell) => cell.id === parentId);
};

export const canConfigureCellSettings = (cell, cells) => {
    if ([GOAL_TASK_COMPLETED, GOAL_EMAIL_LINK_CLICKED, GOAL_EMAIL_CONFIRM].includes(cell.type)) {
        return false;
    }

    let response = false;

    const incomingCells = findIncomingCells(cell.id, cells);

    incomingCells.forEach((source) => {
        if (source.meta?.style === FLOW_TYPE) {
            response = true;
        }
    });

    return response;
};

export const getCellById = (cellId, cells) => {
    return cells.find((cell) => cell.id === cellId);
};

export const isParentCellLocked = (cell, cells) => {
    if (!cells?.filter) return false;

    const parentSequence = cells.find((element) => element.id === cell.meta?.parent);

    return parentSequence?.value?.flowItemsLocked === '1';
};

export const isCellLocked = (cell, cells) => {
    if (cell.type === FLOW_TYPE && cell.value?.methodLocked === '1') return true;

    if (cell.type === EDGE_TYPE) {
        const sourceCell = getCellById(cell.meta.source, cells);

        if (sourceCell?.value?.methodLocked === '1') return true;

        return isParentCellLocked(sourceCell, cells);
    }

    const incomingLockedCells = findLockedIncomingCells(cell.id, cells);

    if (incomingLockedCells.length > 0) {
        return true;
    }

    return isParentCellLocked(cell, cells);
};

export const getLastCellInSequence = (sequenceId, cells) => {
    const flowStartCell = cells.find((cell) => {
        return (cell.meta.parent === sequenceId) && (cell.type === SEQUENCE_START);
    });

    const linkedCellIds = [];
    let nextCell = findOutgoingCells(flowStartCell.meta.id, cells).pop();

    if (!nextCell) return flowStartCell;


    while (nextCell && !linkedCellIds.includes(nextCell.meta.id)) {
        linkedCellIds.push(nextCell.meta.id);
        const linkedCell = findOutgoingCells(nextCell.meta.id, cells).pop();

        if (linkedCell) nextCell = linkedCell;
    }

    return nextCell;
};

export const isValid = (childType, cellType) => {
    const validIncomingFlows = Object.values(VALID_INCOMING_FLOW[cellType]);

    return validIncomingFlows.some((flow) => {
        return flow === childType;
    });
};

export const getValidIncomingFlows = (cell, cells) => {
    const flows = findIncomingCells(cell.id, cells);
    const validIncomingFlows = [];

    flows.forEach((flow) => {
        const children = findChildren(flow.id, cells);

        children.forEach((child) => {
            if (isValid(child.type, cell.type)) {
                validIncomingFlows.push(child);
            }
        });
    });

    return validIncomingFlows;
};

export const areIncomingFlowsValidForCell = (cell, cells) => {
    let result = { isValid: true };
    const validIncomingFlows = getValidIncomingFlows(cell, cells);

    if (validIncomingFlows.length < 1) {
        result = {
            isValid: false,
            messageKey: NO_VALID_INCOMING_FLOW_ERROR[cell.type],
        };
    }

    return result;
};

export const transformCell = (cell) => {
    if (cell.value) {
        if (cell.value?.deepCopy !== undefined) {
            delete cell.value.deepCopy;
        }

        if (cell.value.eventId !== undefined && cell.value.eventId !== null) {
            if (typeof cell.value.eventId === 'string') {
                cell.value.eventId = [cell.value.eventId];
            } else if (!Array.isArray(cell.value.eventId)) {
                cell.value.eventId = [cell.value.eventId.toString()];
            } else {
                cell.value.eventId = cell.value.eventId.map((id) => id.toString());
            }
        }

        if (cell.value.purchaseId !== undefined && cell.value.purchaseId !== null) {
            if (typeof cell.value.purchaseId === 'string') {
                cell.value.purchaseId = [cell.value.purchaseId];
            } else if (!Array.isArray(cell.value.purchaseId)) {
                cell.value.purchaseId = [cell.value.purchaseId.toString()];
            } else {
                cell.value.purchaseId = cell.value.purchaseId.map((id) => id.toString());
            }
        }

        if (typeof cell.value?.purchaseOption === 'string') {
            cell.value.purchaseOption = [cell.value.purchaseOption];
        }

        if (typeof cell.value?.failedPurchaseId === 'string') {
            cell.value.failedPurchaseId = utils.removeLongSuffix(cell.value.failedPurchaseId);
        }
    }

    return {
        id: cell.id,
        type: cell.type,
        value: cell.value,
    };
};

export const checkBrokenCells = async (cells) => {
    const checkedCellTypes = [GOALS.GOAL_TAG_APPLIED, GOALS.GOAL_PRODUCT_PURCHASED, GOALS.GOAL_FAILED_PURCHASE,
        PROCESSES.PROCESS_APPLY_REMOVE_TAG, PROCESSES.PROCESS_MOVE_OPPORTUNITY];
    const payload = cells
        .filter((cell) => { return checkedCellTypes.includes(cell.type); })
        .map(transformCell);

    const { data } = await axios.post(`${BASE_FUNNEL_URL}/checkBrokenCells`, payload);

    return data.filter((cell) => {
        if (cell?.ids?.length > 0) return true;

        return false;
    });
};

const getAllVertexes = (cells) => {
    if (!cells?.filter) return [];

    return cells.filter((cell) => cell.meta?.vertex === '1');
};

const getAllReadyVertexes = (cells) => {
    if (!cells?.filter) return [];

    return cells.filter((cell) => cell.meta?.vertex === '1' && cell.value?.ready === '1');
};

const getDraftReadyVertexIds = (draftCells) => {
    const draftVertexIds = [];

    const draftVertexes = getAllReadyVertexes(draftCells);

    draftVertexes.forEach((vertex) => {
        const vertexId = vertex.id;
        const vertexType = vertex.type;

        if (NON_PROCESS_TYPES.includes(vertexType)) {
            // vertex is a goal, decision diamond or leadsource
            draftVertexIds.push(vertexId);
        } else if (vertexType === FLOW_TYPE) {
            // vertex is a flow
            draftVertexIds.push(vertexId);

            // include processes
            const children = findChildren(vertexId, draftCells);
            const startNodes = children.filter((child) => child.type === SEQUENCE_START);

            startNodes.forEach((startNode) => {
                if (startNode.value?.ready === '1') {
                    draftVertexIds.push(startNode.id);
                }

                let flowNode = startNode;
                let outgoingVertexes = [];

                do {
                    outgoingVertexes = findOutgoingCells(flowNode.id, draftCells);

                    if (outgoingVertexes.length === 1) {
                        [flowNode] = outgoingVertexes;

                        if (flowNode.value?.ready === '1') {
                            draftVertexIds.push(flowNode.id);
                        } else if (TIMER_TYPES.includes(flowNode.type)) {
                            // we don't care for anything following a timer that is not ready
                            break;
                        }
                    } else {
                        // nothing left to process
                        break;
                    }
                } while (outgoingVertexes.length);
            });
        }
    });

    return draftVertexIds;
};

export const hasNewOrModifiedVertexes = (draftCells, publishedCells) => {
    if (!publishedCells?.length) return false;

    const publishedVertexIds = getAllVertexes(publishedCells).map((vertex) => vertex.id);
    const draftReadyVertexes = getAllReadyVertexes(draftCells);

    return draftReadyVertexes.some((vertex) => (NON_PROCESS_TYPES.includes(vertex.type) || vertex.type === FLOW_TYPE)
            && (vertex.value.published !== '1' || !publishedVertexIds.includes(vertex.id)));
};

export const hasRemovedVertexes = (draftCells, publishedCells) => {
    if (!publishedCells?.length) return false;

    const publishedVertexIds = getAllVertexes(publishedCells).map((vertex) => vertex.id);
    const draftReadyVertexIds = getDraftReadyVertexIds(draftCells);

    return publishedVertexIds.some((id) => (!draftReadyVertexIds.includes(id)));
};
